// normalizeCode: si el código termina en “-0”, le saca un segmento “0” de cola. Ej.: 3-1-0 → 3-1.
const normalizeCode = code => {
  const seg = code.split('-')
  if (seg[seg.length - 1] === '0') seg.pop()
  return seg.join('-')
}

// add12: suma elemento a elemento dos arrays de 12 meses.
const add12 = (a = [], b = []) => {
  const out = new Array(12).fill(0)
  for (let i = 0; i < 12; i++) out[i] = (a[i] || 0) + (b[i] || 0)
  return out
}

// isExact98: detecta únicamente las cuentas cuyo 4º segmento es 98 (p. ej. 3-1-1-98).
// No confunde con 3-1-1-98-1 (que tiene 5 segmentos).
const isExact98 = code => {
  const p = code.split('-')
  return p.length === 4 && p[3] === '98'
}
// logicalParentCode: regla de “padre lógico”.
// Si es una cuenta del tipo …-98-* (5+ segmentos), cuélgala del nivel 3 (ej. 3-1-1).
// Si no, usa el padre directo (quita 1 segmento).
const logicalParentCode = code => {
  const parts = code.split('-')
  if (parts.length >= 4 && parts[3] === '98') {
    return parts.slice(0, 3).join('-') // 3-1-1-98-* => 3-1-1
  }
  return parts.slice(0, -1).join('-')
}

export function buildAccountTree(flatAccounts, monthlySums = {}) {
  // 0) Normalizar montos y **descartar** ...-98 exactos
  // Paso 0: normaliza claves de monthlySums (quita un “-0” de cola) y,
  // tal como está ahora, ignora por completo cualquier clave …-98 (4 segmentos).
  const sumsNorm = {}
  for (const [k, arr] of Object.entries(monthlySums)) {
    const nk = normalizeCode(k)
    if (isExact98(nk)) continue // <<-- IGNORAR ...-98 exacto (ni se crea ni se suma)
    sumsNorm[nk] = add12(sumsNorm[nk], arr)
  }

  // 1) normalizar cuentas (quitar trailing -0)
  // crea un arreglo “flat” de cuentas, también quitando un “-0” final en los códigos.
  const flat = flatAccounts.map(({ codeCuenta, nameCuenta }) => {
    const seg = codeCuenta.split('-')
    if (seg[seg.length - 1] === '0') seg.pop()
    return { code: seg.join('-'), name: nameCuenta }
  })

  // 2) filtrar: sacar de 1 segmento y **no generar** ...-98 exactos
  //Paso 2: se queda con cuentas de 2+ segmentos y, tal como está ahora, elimina del árbol las …-98 exactas.
  const filtered = flat
    .filter(({ code }) => code.split('-').length > 1)
    .filter(({ code }) => !isExact98(code)) // <<-- NO crear nodos ...-98

  // 3) crear mapa
  //Paso 3: crea los nodos en un mapa {code → nodo}, con su arreglo de 12 meses y total.
  const map = {}
  filtered.forEach(({ code, name }) => {
    const arr = sumsNorm[code] || Array(12).fill(0)
    map[code] = {
      accountCode: code,
      name,
      subRows: [],
      presupuesto: arr,
      total: arr.reduce((a, b) => a + b, 0)
    }
  })

  // 4: garantiza que existan 3-1 y 3-2 si hay hijos más profundos que los necesiten.
  // ... recopila qué padres de nivel 2 faltan y los crea si no existen
  const nivel2Names = { '3-1': 'Resultado Operacional', '3-2': 'Resultado no Operacional' }
  const parents2 = new Set()
  filtered.forEach(({ code }) => {
    const parts = code.split('-')
    if (parts.length > 2) parents2.add(parts.slice(0, 2).join('-'))
  })
  parents2.forEach(code2 => {
    if (!map[code2]) {
      const arr = sumsNorm[code2] || Array(12).fill(0)
      map[code2] = {
        accountCode: code2,
        name: nivel2Names[code2] || code2,
        subRows: [],
        presupuesto: arr,
        total: arr.reduce((a, b) => a + b, 0)
      }
    }
  })

  // 5) enlazar padre → hijo (con padre lógico para ...-98-*)
  // Paso 5: arma el árbol enlazando cada cuenta con su
  // padre (regla especial para …-98-*; si el padre no existe, cae al nivel 2).
  const roots = []
  Object.keys(map)
    .sort((a, b) => {
      const A = a.split('-').map(Number)
      const B = b.split('-').map(Number)
      for (let i = 0; i < Math.max(A.length, B.length); i++) {
        if ((A[i] || 0) !== (B[i] || 0)) return (A[i] || 0) - (B[i] || 0)
      }
      return 0
    })
    .forEach(code => {
      const parts = code.split('-')
      if (parts.length === 2) {
        roots.push(map[code])
      } else {
        const parent = logicalParentCode(code)
        if (map[parent]) {
          map[parent].subRows.push(map[code])
        } else {
          const parent2 = parts.slice(0, 2).join('-')
          if (map[parent2]) map[parent2].subRows.push(map[code])
        }
      }
    })

  return roots
}
